/**
 * Fetches and processes Lemonbeat trafo measurements for the grid view.
 * Uses the SAME API + processing logic as TrafostationDetails to ensure data consistency.
 */
import {
  getLinkedOutletsByTrafo,
  type LinkedAmqpOutletsResponse,
  type OutletMeasurement,
} from './transformerAmqpService';

export interface TrafoMeasurementData {
  currentL1A: number | null;
  currentL2A: number | null;
  currentL3A: number | null;
  apparentPowerKva: number | null;
  ratingPowerKva: number | null;
  voltageL1V: number | null;
  voltageL2V: number | null;
  voltageL3V: number | null;
}

type PhaseKey = 'L1' | 'L2' | 'L3';

// --- Same helpers as TrafostationDetails ---

const normalizeUnit = (unit?: string | null) => (unit ?? '').trim().toLowerCase();

const convertCurrentToA = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'a') return value;
  if (normalized === 'ka') return value * 1000;
  return value;
};

const convertApparentToKva = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'va') return value / 1000;
  if (normalized === 'kva') return value;
  if (normalized === 'mva') return value * 1000;
  return value / 1000;
};

const getPhaseCurrentMapping = (m: OutletMeasurement): PhaseKey | null => {
  const type = (m.measurementType ?? '').toLowerCase();
  if (type === 'current_phase_a') return 'L1';
  if (type === 'current_phase_b') return 'L2';
  if (type === 'current_phase_c') return 'L3';

  const iec = (m.iecName ?? '').toLowerCase();
  if (iec.includes('.a.phsa')) return 'L1';
  if (iec.includes('.a.phsb')) return 'L2';
  if (iec.includes('.a.phsc')) return 'L3';

  return null;
};

const isTotalApparentMeasurement = (m: OutletMeasurement) => {
  const type = (m.measurementType ?? '').toLowerCase();
  if (type === 'apparent_power_total') return true;
  const iec = (m.iecName ?? '').toLowerCase();
  return iec.includes('totva') && !iec.includes('totvar');
};

const isAverageMeasurement = (m: OutletMeasurement) => {
  const iec = (m.iecName ?? '').toLowerCase();
  if (!iec) return true;
  return !iec.includes('maxmmxu') && !iec.includes('minmmxu');
};

function findTrafoOutlet(data: LinkedAmqpOutletsResponse) {
  if (!data.outlets?.length) return null;
  if (data.trafoOutletId) {
    const byId = data.outlets.find(o => o.id === data.trafoOutletId);
    if (byId) return byId;
  }
  const byFamily = data.outlets.find(o => (o.deviceFamily ?? '').toLowerCase() === 'trafo');
  if (byFamily) return byFamily;
  return data.outlets.find(o => !o.linkedCableId) ?? null;
}

function getLatest(measurements: OutletMeasurement[], predicate: (m: OutletMeasurement) => boolean): OutletMeasurement | null {
  let latest: OutletMeasurement | null = null;
  let latestTs = 0;
  for (const m of measurements) {
    if (!predicate(m)) continue;
    const ts = Date.parse(m.timestamp);
    if (Number.isFinite(ts) && (!latest || ts > latestTs)) {
      latest = m;
      latestTs = ts;
    } else if (!latest) {
      latest = m;
    }
  }
  return latest;
}

function processTrafoData(data: LinkedAmqpOutletsResponse): TrafoMeasurementData {
  const outlet = findTrafoOutlet(data);
  const measurements = outlet?.measurements ?? [];

  // Per-phase current: find latest average measurement per phase
  const currents: Record<PhaseKey, number | null> = { L1: null, L2: null, L3: null };
  const latestPerPhase = new Map<PhaseKey, { measurement: OutletMeasurement; ts: number }>();

  for (const m of measurements) {
    if (!isAverageMeasurement(m)) continue;
    const phase = getPhaseCurrentMapping(m);
    if (!phase) continue;
    const ts = Date.parse(m.timestamp);
    if (!Number.isFinite(ts)) continue;
    const existing = latestPerPhase.get(phase);
    if (!existing || ts > existing.ts) {
      latestPerPhase.set(phase, { measurement: m, ts });
    }
  }

  for (const [phase, entry] of latestPerPhase) {
    const val = Number(entry.measurement.value);
    if (Number.isFinite(val)) {
      currents[phase] = convertCurrentToA(val, entry.measurement.unit);
    }
  }

  // Total apparent power
  const apparentMeasurement = getLatest(measurements, m => isAverageMeasurement(m) && isTotalApparentMeasurement(m));
  let apparentPowerKva: number | null = null;
  if (apparentMeasurement) {
    const val = Number(apparentMeasurement.value);
    if (Number.isFinite(val)) {
      apparentPowerKva = convertApparentToKva(val, apparentMeasurement.unit);
    }
  }

  // Per-phase voltage (same logic as LemonbeatMeasurementsSection)
  const getVoltageV = (type: string): number | null => {
    const m = measurements.find(m => (m.measurementType ?? '').toLowerCase() === type);
    if (m == null) return null;
    const val = Number(m.value);
    return Number.isFinite(val) ? val : null;
  };

  return {
    currentL1A: currents.L1,
    currentL2A: currents.L2,
    currentL3A: currents.L3,
    apparentPowerKva,
    ratingPowerKva: data.ratingPowerKva ?? null,
    voltageL1V: getVoltageV('voltage_phase_a'),
    voltageL2V: getVoltageV('voltage_phase_b'),
    voltageL3V: getVoltageV('voltage_phase_c'),
  };
}

/**
 * Fetches Lemonbeat measurements for all given trafo IDs and returns processed data.
 * Uses the same API and processing logic as TrafostationDetails.
 */
// Trafos without a linked station answer 204 (404 before 1.88.2). Remember that per trafo for a while instead
// of asking again on every 10 s poll: a zone without station measurements otherwise fired
// one failing request per trafo per tick and logged an API error for each.
const NO_LINK_TTL_MS = 10 * 60 * 1000;
const noLinkUntil = new Map<string, number>();

export async function fetchTrafoMeasurements(trafoIds: string[]): Promise<Record<string, TrafoMeasurementData>> {
  const results: Record<string, TrafoMeasurementData> = {};
  const now = Date.now();

  // Fetch in parallel (typically 1-5 trafos per zone)
  const promises = trafoIds.map(async (trafoId) => {
    const skipUntil = noLinkUntil.get(trafoId);
    if (skipUntil !== undefined && skipUntil > now) {
      return;
    }
    try {
      const linked = await getLinkedOutletsByTrafo(trafoId);
      if (!linked) {
        noLinkUntil.set(trafoId, now + NO_LINK_TTL_MS);
        return;
      }
      noLinkUntil.delete(trafoId);
      results[trafoId] = processTrafoData(linked);
    } catch (error: unknown) {
      // 404 = no Lemonbeat device linked — expected, skip silently
      const status = error != null && typeof error === 'object' && 'status' in error ? (error as { status: number }).status : undefined;
      if (status === 404) {
        noLinkUntil.set(trafoId, now + NO_LINK_TTL_MS);
      } else {
        console.warn(`[TrafoMeasurement] Unexpected error fetching trafo ${trafoId}:`, error);
      }
    }
  });

  await Promise.all(promises);
  return results;
}
